import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import isAfter from 'date-fns/isAfter'

import { shallowEqual, useSelector } from 'react-redux'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { canSeeLockDate } from 'data/permissions/permissionsSelectors'
import { createDateFromString, formatDateToFEFormat } from 'utils/date'
import { isAccountingLockFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { useLazyGetAccountingPeriodLockQuery } from 'api/accounting-period/accountingPeriodCrud'

export const AccountingLockContext = createContext()

export const AccountingLockProvider = ({ children }) => {
  const flags = useFlags()
  const [getAccountingPeriodLock, { data, isFetching: isGetAccountingPeriodLockLoading }] = useLazyGetAccountingPeriodLockQuery()
  const isAccountingLockEnabled = isAccountingLockFeatureAvailable({ flags })
  const seeLockDate = useSelector(canSeeLockDate, shallowEqual)

  const hasViewPermission = useMemo(() => isAccountingLockEnabled && seeLockDate, [isAccountingLockEnabled, seeLockDate])
  const isLocked = useMemo(() => get(data, 'locked'), [data])
  const lockDate = useMemo(() => (isLocked ? get(data, 'date') : ''), [isLocked, data])
  const lockDateFormatted = useMemo(() => createDateFromString(lockDate), [lockDate])
  const lockDateToFEFormat = useMemo(() => formatDateToFEFormat(lockDateFormatted), [lockDateFormatted])

  const checkIsLocked = useCallback(
    date => {
      if (!isAccountingLockEnabled || !isLocked || !date) {
        return false
      }

      // We can also use isBefore() || isSameDay() instead of !isAfter()
      // We can also use parseISO() instead of createDateFromString()
      return !isAfter(createDateFromString(date), lockDateFormatted)
    },
    [isAccountingLockEnabled, isLocked, lockDate]
  )

  useEffect(() => {
    if (isAccountingLockEnabled) {
      // Don't check view permission
      getAccountingPeriodLock().unwrap().catch(handleError)
    }
  }, [isAccountingLockEnabled])

  return (
    <AccountingLockContext.Provider
      value={{
        isAccountingLockEnabled,
        hasViewPermission,
        isGetAccountingPeriodLockLoading,
        isLocked,
        lockDate,
        lockDateFormatted,
        lockDateToFEFormat,
        checkIsLocked,
      }}
    >
      {children}
    </AccountingLockContext.Provider>
  )
}

AccountingLockProvider.propTypes = {
  children: PropTypes.node,
}

export default AccountingLockProvider

export const useAccountingLockValue = () => useContext(AccountingLockContext)
